export const EARLY_ECPM_LIMIT = 10;

export type EarlyEcpmFormat = 'rewarded' | 'interstitial';

export type EarlyEcpmLoad = {
  index: number;
  ecpm: number;
  format: EarlyEcpmFormat;
  network?: string;
  precision?: string;
  ad_unit?: string;
  at: string;
};

export type EarlyEcpmState = {
  loads: EarlyEcpmLoad[];
  sent: number;
  complete: boolean;
};

export type EarlyEcpmSender = ( loads: { index: number; ecpm: number }[] ) => Promise<{ complete: boolean }>;

function fullscreenFormat( value: unknown ): EarlyEcpmFormat | null
{
  const raw = String( value ?? '' ).toLowerCase();
  if ( raw.includes( 'reward' ) ) return 'rewarded';
  if ( raw.includes( 'inter' ) ) return 'interstitial';
  return null;
}

function optionalText( value: unknown ): string | undefined
{
  const text = value == null ? '' : String( value ).trim();
  return text || undefined;
}

export class EarlyEcpmQueue
{
  private state: EarlyEcpmState | null = null;
  private in_flight = false;

  constructor(
    private readonly storage_key: string,
    private readonly send: EarlyEcpmSender,
    private readonly on_recorded: ( load: EarlyEcpmLoad ) => void = () => {},
  ) {}

  snapshot(): Readonly<EarlyEcpmState>
  {
    return this.read();
  }

  record( detail: Record<string, unknown> ): EarlyEcpmLoad | null
  {
    const current = this.read();
    if ( current.complete || current.loads.length >= EARLY_ECPM_LIMIT ) return null;

    const format = fullscreenFormat( detail.format );
    const revenue = Number( detail.revenue );
    if ( !format || detail.revenue == null || !Number.isFinite( revenue ) || revenue < 0 ) return null;

    const load: EarlyEcpmLoad = {
      index: current.loads.length + 1,
      ecpm: Math.round( revenue * 1000 * 10000 ) / 10000,
      format,
      network: optionalText( detail.sourceName ),
      precision: optionalText( detail.revenuePrecision ),
      ad_unit: optionalText( detail.adUnitId ),
      at: new Date().toISOString(),
    };
    current.loads.push( load );
    this.write();
    this.on_recorded( load );
    void this.flush();
    return load;
  }

  async flush(): Promise<void>
  {
    const current = this.read();
    if ( this.in_flight || current.complete || current.sent >= current.loads.length ) return;

    this.in_flight = true;
    const batch = current.loads.slice( current.sent );
    let delivered = false;
    try
    {
      const result = await this.send( batch.map( load => ( { index: load.index, ecpm: load.ecpm } ) ) );
      current.sent = Math.max( current.sent, batch[batch.length - 1].index );
      if ( result.complete ) current.complete = true;
      this.write();
      delivered = true;
    }
    catch {}
    finally
    {
      this.in_flight = false;
    }

    if ( delivered && !current.complete && current.sent < current.loads.length ) await this.flush();
  }

  reset(): void
  {
    this.state = { loads: [], sent: 0, complete: false };
    try { localStorage.removeItem( this.storage_key ); }
    catch {}
  }

  private read(): EarlyEcpmState
  {
    if ( this.state ) return this.state;
    try
    {
      const raw = JSON.parse( localStorage.getItem( this.storage_key ) ?? 'null' ) as Partial<EarlyEcpmState> | null;
      const loads = Array.isArray( raw?.loads ) ? raw.loads.slice( 0, EARLY_ECPM_LIMIT ) : [];
      this.state = {
        loads,
        sent: Math.min( Math.max( 0, Number( raw?.sent ) || 0 ), loads.length ),
        complete: raw?.complete === true,
      };
    }
    catch
    {
      this.state = { loads: [], sent: 0, complete: false };
    }
    return this.state;
  }

  private write(): void
  {
    try { localStorage.setItem( this.storage_key, JSON.stringify( this.read() ) ); }
    catch {}
  }
}
